import { createClient, RealtimePostgresInsertPayload, SupabaseClient } from '@supabase/supabase-js';
import { Database, ChatRoomRow, MessageRow } from '@libs/models';

export interface SupabasePagination<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

export class SupabaseService {
  private static instance: SupabaseService;
  private client: SupabaseClient<Database>;

  private constructor(url: string, key: string) {
    this.client = createClient<Database>(url, key);
  }

  public static getInstance(url: string, key: string): SupabaseService {
    if (!SupabaseService.instance) {
      SupabaseService.instance = new SupabaseService(url, key);
    }

    return SupabaseService.instance;
  }

  public getClient(): SupabaseClient<Database> {
    return this.client;
  }

  async createChatRoom(
    postId?: number,
    jobseekerUserId?: number,
    employerUserId?: number,
    jobseekerName?: string,
    employerName?: string,
    jobTitle?: string,
    rooftopName?: string,
  ): Promise<ChatRoomRow> {
    if (!employerUserId || !jobseekerUserId || !postId) {
      throw new Error('Employer user ID, jobseeker user ID and post ID are required');
    }

    const { data, error } = await this.client
      .from('chat_rooms')
      .insert({
        post_id: postId,
        employer_user_id: employerUserId,
        jobseeker_user_id: jobseekerUserId,
        jobseeker_name: jobseekerName,
        employer_name: employerName,
        job_title: jobTitle,
        rooftop_name: rooftopName,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  async getChatRoomById(chatRoomId?: number): Promise<ChatRoomRow> {
    if (!chatRoomId) {
      throw new Error('Chat room ID is required');
    }

    const { data, error } = await this.client.from('chat_rooms').select('*').eq('id', chatRoomId).single();

    if (error) {
      throw error;
    }

    return data;
  }

  async getChatRoomsForUser(
    userId: number,
    role: string,
    limit = 10,
    page = 0,
  ): Promise<SupabasePagination<ChatRoomRow>> {
    const column = role === 'employer' ? 'employer_user_id' : 'jobseeker_user_id';

    const { data, error, count } = await this.client
      .from('chat_rooms')
      .select('*', { count: 'exact' })
      .eq(column, userId)
      .order('last_message_sent_at', { ascending: false })
      .range(page * limit, (page + 1) * limit - 1);

    if (error) {
      return {
        items: [],
        total: 0,
        page,
        limit,
      };
    }

    return {
      items: data,
      total: count || 0,
      page,
      limit,
    };
  }

  async getChatRoomForEmployerPostAndJobseeker(
    postId?: number,
    jobseekerUserId?: number,
    employerUserId?: number,
  ): Promise<ChatRoomRow | null> {
    if (!postId || !jobseekerUserId || !employerUserId) {
      return null;
    }

    const { data, error } = await this.client
      .from('chat_rooms')
      .select('*')
      .eq('post_id', postId)
      .eq('jobseeker_user_id', jobseekerUserId)
      .eq('employer_user_id', employerUserId)
      .single();

    if (error) {
      return null;
    }

    return data;
  }

  async updateChatRoom(chatRoomId: number, updateData: Partial<ChatRoomRow>): Promise<ChatRoomRow | null> {
    const { data, error } = await this.client
      .from('chat_rooms')
      .update(updateData)
      .eq('id', chatRoomId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  async sendMessage(chatRoomId: number, message: string, senderUserId?: number): Promise<MessageRow | null> {
    if (!senderUserId) {
      return null;
    }

    const { data: newMessage, error: messageError } = await this.client
      .from('messages')
      .insert({
        chat_room_id: chatRoomId,
        sender_user_id: senderUserId,
        message: message,
      })
      .select()
      .single();

    if (messageError) {
      throw messageError;
    }

    const { error: updateError } = await this.client
      .from('chat_rooms')
      .update({
        last_message_id: newMessage.id,
        last_message_text: message,
        last_message_sent_at: new Date().toISOString(),
      })
      .eq('id', chatRoomId);

    if (updateError) {
      throw updateError;
    }

    return newMessage;
  }

  async getMessages(chatRoomId: number, limit = 15, page = 0): Promise<SupabasePagination<MessageRow>> {
    const { data, error, count } = await this.client
      .from('messages')
      .select('*', { count: 'exact' })
      .eq('chat_room_id', chatRoomId)
      .order('sent_at', { ascending: false })
      .range(page * limit, (page + 1) * limit - 1);

    if (error) {
      return {
        items: [],
        total: 0,
        page,
        limit,
      };
    }

    return {
      items: data,
      total: count || 0,
      page,
      limit,
    };
  }

  async findChatRoomsWithUnreadMessages(userId?: number, role?: string): Promise<boolean> {
    if (!userId || !role) {
      return false;
    }

    const column = role === 'employer' ? 'employer_user_id' : 'jobseeker_user_id';
    const unreadColumn = role === 'employer' ? 'employer_has_unread' : 'jobseeker_has_unread';

    const { data, error } = await this.client
      .from('chat_rooms')
      .select('id')
      .eq(column, userId)
      .eq(unreadColumn, true)
      .limit(1);

    if (error) {
      throw error;
    }

    return data !== null && data.length > 0;
  }

  async deactivateUser(userId: number, type: 'employer' | 'jobseeker') {
    const idColumn = type === 'employer' ? 'employer_user_id' : 'jobseeker_user_id';
    const nameColumn = type === 'employer' ? 'employer_name' : 'jobseeker_name';
    const rooftopNameColumn = type === 'employer' ? 'rooftop_name' : null;

    const updateData = {
      [nameColumn]: 'Deleted User',
    };

    if (rooftopNameColumn) {
      updateData[rooftopNameColumn] = 'Deleted Rooftop';
    }

    const { data, error } = await this.client.from('chat_rooms').update(updateData).eq(idColumn, userId);

    if (error) {
      throw error;
    }

    return data;
  }

  subscribeToMessages(chatRoomId: number, callback: (payload: RealtimePostgresInsertPayload<MessageRow>) => void) {
    return this.client
      .channel(`chat_room_${chatRoomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_room_id=eq.${chatRoomId}`,
        },
        callback,
      )
      .subscribe();
  }

  subscribeToRoomUpdates(userId: number | undefined, callback: (payload: any) => void) {
    if (!userId) {
      return;
    }

    return this.client
      .channel(`user_rooms_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_rooms',
          filter: `employer_user_id=eq.${userId}`,
        },
        callback,
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_rooms',
          filter: `jobseeker_user_id=eq.${userId}`,
        },
        callback,
      )
      .subscribe();
  }
}
